import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || categories[0].slug;
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [searchParams]);

  const activeCategory = categories.find((c) => c.slug === activeTab);
  const categoryArticles = getArticlesByCategory(activeTab);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://xiaomablock.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: activeCategory?.name || '分类',
        item: `https://xiaomablock.cn/category?tab=${activeTab}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />
      <main>
        <section className="pt-24 md:pt-28 pb-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors no-underline">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium">ASMR 分类</span>
            </nav>

            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                助眠 ASMR <strong className="text-primary-600">内容分类</strong>
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                选择您感兴趣的 ASMR 类型，浏览精选助眠音频与视频推荐内容
              </p>
            </div>

            <div className="flex justify-center mb-12">
              <div className="inline-flex bg-background-100 rounded-full p-1 overflow-x-auto max-w-full">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category?tab=${cat.slug}`}
                    onClick={() => {
                      setActiveTab(cat.slug);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap no-underline ${
                      activeTab === cat.slug
                        ? 'bg-background-50 text-primary-600 shadow-sm'
                        : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {activeCategory && (
              <div className="mb-14">
                <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-8">
                  <img
                    src={activeCategory.image}
                    alt={`${activeCategory.name} ASMR 助眠`}
                    title={`${activeCategory.name} ASMR 助眠音频推荐`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm">
                        <i className={`${activeCategory.icon} text-background-50 text-lg`}></i>
                      </span>
                      <h2 className="text-xl md:text-2xl font-heading font-semibold text-background-50">
                        {activeCategory.name}
                      </h2>
                    </div>
                    <p className="text-sm text-background-50/80 max-w-xl">
                      {activeCategory.description}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 no-underline cursor-pointer"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          title={`${article.title} - 助眠 ASMR`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 line-clamp-2 mb-4">{article.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span>{article.date}</span>
                          <span>{article.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}

                  {categoryArticles.length === 0 && (
                    <div className="col-span-full text-center py-16">
                      <i className="ri-inbox-line text-4xl text-foreground-300 mb-3 block"></i>
                      <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}